import React from 'react';
import { getResourceColor, getInitials } from '../../../../utils/resourceUtils';

/**
 * ResourceCard Component
 * A generic card to display resources (workers, vehicles, kolonnen) in a sidebar or list.
 * 
 * @param {Object} props
 * @param {Object} props.resource - The resource data object.
 * @param {string} props.type - Type of resource: 'worker' | 'vehicle' | 'kolonne'.
 * @param {boolean} [props.isAssigned=false] - Whether the resource is currently assigned/selected.
 * @param {boolean} [props.isEditMode=false] - Whether to show action buttons (add/remove).
 * @param {Object} [props.status] - Optional status object { label, dotClass }.
 * @param {Object} [props.conflict] - Optional conflict data.
 * @param {Function} [props.onToggle] - Callback for the add/remove button.
 * @param {Function} [props.onDragStart] - Callback for drag-and-drop.
 * @param {Function} [props.onDoubleClick] - Callback for double-click action.
 * @param {Function} [props.onConflictHover] - Callback for showing conflict tooltips.
 * @param {Function} [props.onConflictHoverEnd] - Callback for hiding conflict tooltips.
 * @param {Function} [props.onOpenConflictOverview] - Callback to open the conflict center.
 * @returns {JSX.Element}
 */
export default function ResourceCard({
    resource,
    type,
    isAssigned = false,
    isEditMode = false,
    isDraggable = true,
    status,
    conflict,
    onToggle,
    onDragStart,
    onDoubleClick,
    onConflictHover,
    onConflictHoverEnd,
    onOpenConflictOverview
}) {
    const handleDragStart = (e) => {
        if (!isDraggable) {
            e.preventDefault();
            return;
        }

        const dragData = {
            type,
            id: resource.id,
            name: resource.name || resource.license_plate,
            role: resource.role || ''
        };
        
        if (type === 'vehicle') {
            dragData.license_plate = resource.license_plate;
            dragData.internal_name = resource.internal_name;
        } else if (type === 'kolonne') {
            dragData.members = resource.members;
        }
        
        e.dataTransfer.setData('application/json', JSON.stringify(dragData));
        e.dataTransfer.effectAllowed = 'copy';
        
        if (onDragStart) {
            onDragStart(resource);
        }
    };

    const handleDoubleClick = (e) => {
        if (onDoubleClick) {
            e.preventDefault();
            onDoubleClick(resource.id, type);
        }
    };

    const getAvatarContent = () => {
        if (type === 'worker') return getInitials(resource.name);
        if (type === 'vehicle') return '🚛';
        if (type === 'kolonne') return resource.code || 'K';
        return '?';
    };

    const getAvatarBg = () => {
        if (type === 'worker') return getResourceColor(resource);
        if (type === 'kolonne') return '#3b82f6'; // Brand Blue for Kolonnen
        return '#f3f4f6'; // Neutral for vehicles
    };

    const getName = () => {
        if (type === 'vehicle') return resource.license_plate || '—';
        return resource.name;
    };

    const getSubText = () => {
        if (type === 'worker') return resource.category_name || resource.role || 'Monteur';
        if (type === 'vehicle') {
            const parts = [];
            if (resource.internal_name && resource.internal_name !== resource.license_plate) parts.push(resource.internal_name);
            if (resource.global_role || resource.role) parts.push(resource.global_role || resource.role);
            return parts.join(' • ') || null;
        }
        if (type === 'kolonne') {
            const wCount = resource.members?.filter(m => m.type === 'worker').length || 0;
            const vCount = resource.members?.filter(m => m.type === 'vehicle').length || 0;
            return `${wCount} 👤 • ${vCount} 🚛`;
        }
        return null;
    };

    return (
        <div
            className={`resource-card ${isAssigned ? 'is-assigned' : ''} ${!isDraggable ? 'no-drag' : ''}`}
            draggable={isDraggable}
            onDragStart={handleDragStart}
            onDoubleClick={handleDoubleClick}
        >
            <div className="resource-avatar" style={{ background: getAvatarBg() }}>
                {getAvatarContent()}
            </div>
            
            <div className="resource-info">
                <div className="resource-name flex items-center gap-1.5">
                    {getName()}
                    {status && (
                        <span 
                            className={`resource-status-dot ${status.dotClass || ''}`} 
                            style={status.dotColor ? { backgroundColor: status.dotColor } : undefined}
                            title={status.label} 
                        />
                    )}
                </div>
                {getSubText() && <div className="resource-sub">{getSubText()}</div>}
            </div>

            {conflict && (
                <div 
                    className="resource-conflict-badge"
                    onMouseEnter={(e) => onConflictHover && onConflictHover(conflict, null, e)}
                    onMouseLeave={() => onConflictHoverEnd && onConflictHoverEnd()}
                    onClick={(e) => {
                        e.stopPropagation();
                        onOpenConflictOverview && onOpenConflictOverview({ type, id: resource.id });
                    }}
                >
                    <span className="resource-conflict-icon animate-pulse">⚠️</span>
                </div>
            )}

            {isEditMode && onToggle && (
                <button 
                    type="button"
                    className={`btn-quick-add ${isAssigned ? 'added' : ''}`}
                    onClick={(e) => {
                        e.stopPropagation();
                        onToggle(resource, type);
                    }}
                    onDoubleClick={(e) => e.stopPropagation()}
                    aria-label={isAssigned ? "Entfernen" : "Hinzufügen"}
                >
                    {isAssigned ? '−' : '+'}
                </button>
            )}
        </div>
    );
}
